package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.LabourInclusionRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.first
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

/** SH-206: a person's "Count in labour cost and SPMH" at a workplace this month, as the employee form shows it. */
data class LabourInclusionStatus(
    /** They count this month. */
    val counts: Boolean,
    /** No setting applies: [counts] is the default. */
    val isDefault: Boolean,
    /** They work here without a wage: counted, their hours count at no cost. */
    val worksWithoutWage: Boolean
)

/** The workplace's own today ("YYYY-MM"), from its time zone (the device's when unknown). */
private fun Workplace.monthNow(): String {
    val zone = runCatching { TimeZone.of(settings.timezone) }.getOrElse { TimeZone.currentSystemDefault() }
    return LabourInclusion.monthOf(AppClock.todayIn(zone))
}

/**
 * SH-206: [userId]'s switch at a workplace this month. Owners and financial managers only
 * (EDIT_WORKPLACE_FINANCES, the employee form's finances); null for anyone else, nothing read.
 */
class GetLabourInclusionUseCase(
    private val repository: LabourInclusionRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val caller: CallerCapabilities? = null,
    private val month: ((Workplace) -> String)? = null
) {
    suspend operator fun invoke(workplaceId: String, userId: String): Result<LabourInclusionStatus?> = runCatching {
        if (!caller.allows(workplaceId, Capability.EDIT_WORKPLACE_FINANCES)) return@runCatching null
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val periods = async { repository.get(workplaceId, userId).getOrThrow() }
            val profile = async { userRepository.getUserProfile(userId).getOrNull() }
            val terms = async { userRepository.getSensitiveUserData(userId).getOrNull()?.financialEngagements?.get(workplaceId) }
            val wp = workplace.await()
            val now = month?.invoke(wp) ?: wp.monthNow()
            val facts = LabourInclusion.factsOf(userId, wp, profile.await()?.engagements?.get(workplaceId)?.permission, terms.await(), now)
            val setting = LabourInclusion.settingIn(periods.await(), now)
            val counts = setting ?: LabourInclusion.byDefault(facts)
            LabourInclusionStatus(counts = counts, isDefault = setting == null, worksWithoutWage = counts && facts.worksWithoutWage)
        }
    }
}

/**
 * SH-206: sets whether [userId]'s shifts count in the workplace's labour cost, labour % and SPMH,
 * from this month on (the workplace's month; earlier months keep what they had). Owners and
 * financial managers (EDIT_WORKPLACE_FINANCES, as firestore.rules allow). The months still open
 * from this one on are written again (their daily records, and the totals the server adds up),
 * and the screens showing labour cost read again.
 */
class SetLabourInclusionUseCase(
    private val repository: LabourInclusionRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val rosterRepository: RosterRepository? = null,
    private val regenerateDailyRecords: RegenerateDailyRecordsUseCase? = null,
    private val changes: PayTermsChanges? = null,
    private val caller: CallerCapabilities? = null,
    private val month: ((Workplace) -> String)? = null
) {
    suspend operator fun invoke(workplaceId: String, userId: String, counts: Boolean, updatedBy: String): Result<LabourInclusionStatus> = runCatching {
        if (!caller.allows(workplaceId, Capability.EDIT_WORKPLACE_FINANCES)) {
            throw IllegalStateException("Only owners and financial managers decide who counts in the labour cost")
        }
        val (wp, periods, facts) = coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val periods = async { repository.get(workplaceId, userId).getOrThrow() }
            val profile = async { userRepository.getUserProfile(userId).getOrNull() }
            val terms = async { userRepository.getSensitiveUserData(userId).getOrNull()?.financialEngagements?.get(workplaceId) }
            val wp = workplace.await()
            val now = month?.invoke(wp) ?: wp.monthNow()
            Triple(wp, periods.await(), LabourInclusion.factsOf(userId, wp, profile.await()?.engagements?.get(workplaceId)?.permission, terms.await(), now))
        }
        val now = month?.invoke(wp) ?: wp.monthNow()
        val updated = LabourInclusion.withSetting(periods, counts, now, facts)
        if (updated != periods) {
            repository.save(workplaceId, userId, updated, updatedBy).getOrThrow()
            rewriteOpenMonths(workplaceId, now)
        }
        changes?.changed()
        val setting = LabourInclusion.settingIn(updated, now)
        LabourInclusionStatus(counts, isDefault = setting == null, worksWithoutWage = counts && facts.worksWithoutWage)
    }

    /** The months still open from [fromMonth] on: their daily records follow (best effort; opening a month computes it anyway). */
    private suspend fun rewriteOpenMonths(workplaceId: String, fromMonth: String) {
        val rosters = rosterRepository ?: return
        val regenerate = regenerateDailyRecords ?: return
        rosters.observeRosters(workplaceId).first()
            .filter { it.status != RosterStatus.LOCKED && it.status != RosterStatus.ARCHIVED }
            .filter { LabourInclusion.monthOf(it.year, it.month) >= fromMonth }
            .forEach { roster -> runCatching { regenerate(workplaceId, roster.id, roster.year, roster.month).getOrThrow() } }
    }
}
